import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaArrowLeft,
  FaInstagram,
  FaFacebook,
  FaTwitter,
  FaYoutube,
  FaLinkedin,
  FaTiktok,
  FaTelegram,
  FaGlobe,
  FaExternalLinkAlt,
  FaHeart,
  FaCommentDots,
  FaShare,
  FaPlay,
  FaUsers,
  FaImage,
  FaCheckCircle,
  FaDownload,
  FaCamera,
  FaChartLine,
} from 'react-icons/fa';

import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechButtonGhost,
  StatusPill,
  StatStrip,
  StatTile,
  StatLabel,
  StatValue,
  StatSub,
  Empty,
  Loading,
  ErrorBox,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import {
  getSocialProfile,
  listSocialPosts,
  fetchProfileComments,
  screenshotOcrComments,
  listDummyAccountsForOcr,
} from '../../services/socialService';

const PLATFORM_META = {
  instagram: { label: 'Instagram',   Icon: FaInstagram, color: '#e1306c' },
  facebook:  { label: 'Facebook',    Icon: FaFacebook,  color: '#1877f2' },
  twitter:   { label: 'X (Twitter)', Icon: FaTwitter,   color: '#1da1f2' },
  x:         { label: 'X (Twitter)', Icon: FaTwitter,   color: '#1da1f2' },
  youtube:   { label: 'YouTube',     Icon: FaYoutube,   color: '#ff0000' },
  linkedin:  { label: 'LinkedIn',    Icon: FaLinkedin,  color: '#0a66c2' },
  tiktok:    { label: 'TikTok',      Icon: FaTiktok,    color: '#000000' },
  telegram:  { label: 'Telegram',    Icon: FaTelegram,  color: '#26a5e4' },
};

function platformMeta(p) {
  return PLATFORM_META[p] || { label: p || 'Otro', Icon: FaGlobe, color: '#888' };
}

const fmtRel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  const h = diff / 36e5;
  if (h < 1) return 'hace <1h';
  if (h < 24) return `hace ${Math.round(h)}h`;
  const days = h / 24;
  if (days < 7) return `hace ${Math.round(days)}d`;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

const fmtAbs = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const fmtNum = (n) => {
  if (n == null) return '—';
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  if (x >= 1_000_000) return (x / 1_000_000).toFixed(1) + 'M';
  if (x >= 1_000) return (x / 1_000).toFixed(1) + 'k';
  return String(x);
};

// CDNs de IG/FB/YT/Twitter rechazan requests sin Referer válido. Pasamos
// la imagen por nuestro endpoint /api/social-image-proxy que inyecta el
// Referer correcto por host. El endpoint validates con whitelist de hosts.
const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';
const PROXY_HOSTS = [
  /\.cdninstagram\.com$/i,
  /\.fbcdn\.net$/i,
  /(^|\.)ggpht\.com$/i,
  /(^|\.)ytimg\.com$/i,
  /pbs\.twimg\.com$/i,
  /(^|\.)twimg\.com$/i,
  /(^|\.)tiktokcdn\.com$/i,
];
function proxiedImage(rawUrl) {
  if (!rawUrl) return null;
  try {
    const u = new URL(rawUrl);
    if (PROXY_HOSTS.some((re) => re.test(u.host))) {
      return `${API_BASE}/social-image-proxy?url=${encodeURIComponent(rawUrl)}`;
    }
    return rawUrl;
  } catch {
    return rawUrl;
  }
}

/* ---------- styled ---------- */

const PostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PostCard = styled.article`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .media {
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    background: rgba(255, 255, 255, 0.03);
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .play {
      position: absolute;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: rgba(0, 0, 0, 0.55);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.2rem;
      pointer-events: none;
    }
    .ph {
      color: var(--text-disabled);
      font-family: ${mono};
      font-size: 0.62rem;
      letter-spacing: 0.18em;
      text-transform: uppercase;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      svg { font-size: 1.6rem; }
    }
  }

  .meta-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.55rem 0.9rem;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--text-disabled);
    border-bottom: 1px solid ${borderSoft};

    .ext {
      margin-left: auto;
      color: var(--text-disabled);
      &:hover { color: ${accent}; }
    }
  }

  .body {
    padding: 0.75rem 0.9rem 0.5rem;
    color: var(--text-primary);
    font-size: 0.88rem;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    flex: 1;

    max-height: 9.5em;
    overflow: hidden;
    position: relative;

    &.expanded { max-height: none; }
  }

  .more {
    background: none;
    border: none;
    color: ${accent};
    font-size: 0.78rem;
    padding: 0 0.9rem 0.6rem;
    cursor: pointer;
    text-align: left;

    &:hover { text-decoration: underline; }
  }

  .metrics {
    padding: 0.6rem 0.9rem;
    background: rgba(0, 0, 0, 0.18);
    border-top: 1px solid ${borderSoft};
    display: flex;
    gap: 1.1rem;
    align-items: center;
    font-family: ${mono};
    font-size: 0.78rem;
    color: var(--text-secondary);

    span {
      display: inline-flex;
      align-items: center;
      gap: 5px;

      svg { font-size: 0.85rem; opacity: 0.75; }
    }
  }

  .toggle-comments {
    width: 100%;
    background: transparent;
    border: none;
    border-top: 1px solid ${borderSoft};
    color: var(--text-secondary);
    padding: 0.6rem;
    cursor: pointer;
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;

    &:hover { color: ${accent}; background: rgba(0, 180, 216, 0.04); }
    &[disabled] {
      cursor: default;
      color: var(--text-disabled);
      &:hover { color: var(--text-disabled); background: transparent; }
    }
  }

  .comments {
    border-top: 1px solid ${borderSoft};
    padding: 0.7rem 0.9rem;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    background: rgba(0, 180, 216, 0.025);
  }

  .comment {
    font-size: 0.82rem;
    color: var(--text-secondary);
    line-height: 1.45;

    .author {
      color: var(--text-primary);
      font-weight: 500;
      margin-right: 6px;
    }
    .when {
      font-family: ${mono};
      font-size: 0.55rem;
      letter-spacing: 0.14em;
      color: var(--text-disabled);
      margin-left: 8px;
      text-transform: uppercase;
    }
    .likes {
      font-family: ${mono};
      font-size: 0.6rem;
      color: var(--text-disabled);
      margin-left: 8px;
    }
  }
`;

const PostItem = ({ post, onOcr, ocrLoading, ocrAvailable }) => {
  const [expanded, setExpanded] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const comments = Array.isArray(post.comments) ? post.comments : [];
  const isVideo = post.media_kind === 'video';
  const text = post.text || '';

  return (
    <PostCard>
      <div className="media">
        {post.media_url ? (
          <>
            <img
              src={proxiedImage(post.media_url)}
              alt=""
              loading="lazy"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {isVideo && <span className="play"><FaPlay /></span>}
          </>
        ) : (
          <div className="ph"><FaImage /> {post.media_kind || 'texto'}</div>
        )}
      </div>
      <div className="meta-row">
        <span>{fmtAbs(post.posted_at)}</span>
        {post.is_political === true && (
          <span style={{
            background: 'rgba(0, 180, 216, 0.15)',
            color: accent,
            padding: '1px 6px',
            borderRadius: 3,
            fontSize: '0.55rem',
            letterSpacing: '0.14em',
            border: '1px solid rgba(0, 180, 216, 0.4)',
          }}>POLÍTICO</span>
        )}
        {post.topic && post.topic !== 'otro' && (
          <span style={{
            background: 'rgba(255, 255, 255, 0.05)',
            color: 'var(--text-secondary)',
            padding: '1px 6px',
            borderRadius: 3,
            fontSize: '0.55rem',
            letterSpacing: '0.14em',
          }}>{post.topic.replace(/_/g, ' ')}</span>
        )}
        {(post.positive_count > 0 || post.negative_count > 0 || post.neutral_count > 0) && (
          <span style={{ fontFamily: mono, fontSize: '0.62rem', color: 'var(--text-disabled)', display: 'inline-flex', gap: 4 }}>
            <span style={{ color: 'var(--alert-success, #38d39f)' }}>+{post.positive_count || 0}</span>
            <span>/</span>
            <span>{post.neutral_count || 0}</span>
            <span>/</span>
            <span style={{ color: 'var(--alert-error)' }}>−{post.negative_count || 0}</span>
          </span>
        )}
        {post.url && (
          <a className="ext" href={post.url} target="_blank" rel="noopener noreferrer" title={post.url}>
            <FaExternalLinkAlt />
          </a>
        )}
      </div>
      <div className={`body ${expanded ? 'expanded' : ''}`}>
        {text || <em style={{ color: 'var(--text-disabled)' }}>(sin texto)</em>}
      </div>
      {text.length > 240 && !expanded && (
        <button type="button" className="more" onClick={() => setExpanded(true)}>Ver más</button>
      )}
      <div className="metrics">
        {fmtNum(post.likes) !== '—' && <span><FaHeart /> {fmtNum(post.likes)}</span>}
        {fmtNum(post.comments_count) !== '—' && <span><FaCommentDots /> {fmtNum(post.comments_count)}</span>}
        {fmtNum(post.shares) !== '—' && <span><FaShare /> {fmtNum(post.shares)}</span>}
      </div>
      <div style={{ display: 'flex', borderTop: `1px solid ${borderSoft}` }}>
        <button
          type="button"
          className="toggle-comments"
          onClick={() => setShowComments((v) => !v)}
          disabled={comments.length === 0}
          style={{ flex: 1, borderTop: 0 }}
        >
          {comments.length === 0
            ? 'Sin comentarios indexados'
            : showComments
              ? `Ocultar (${comments.length})`
              : `Ver comentarios (${comments.length})`}
        </button>
        {ocrAvailable && post.url && (
          <button
            type="button"
            className="toggle-comments"
            onClick={() => onOcr(post)}
            disabled={ocrLoading === post.id}
            title="Screenshot autenticado + OCR para extraer comentarios"
            style={{ flex: '0 0 auto', padding: '0.6rem 0.9rem', borderTop: 0, borderLeft: `1px solid ${borderSoft}` }}
          >
            📸 {ocrLoading === post.id ? 'OCR…' : 'OCR'}
          </button>
        )}
      </div>
      {showComments && comments.length > 0 && (
        <div className="comments">
          {comments.map((c) => {
            const sentDot = c.sentiment === 'positive'
              ? { bg: 'rgba(56, 211, 159, 0.18)', fg: 'var(--alert-success, #38d39f)', label: '+' }
              : c.sentiment === 'negative'
              ? { bg: 'rgba(239, 68, 68, 0.18)', fg: 'var(--alert-error)', label: '−' }
              : c.sentiment === 'neutral'
              ? { bg: 'rgba(160, 160, 160, 0.18)', fg: 'var(--text-secondary)', label: '·' }
              : null;
            return (
              <div className="comment" key={c.id}>
                {sentDot && (
                  <span
                    title={`Sentimiento: ${c.sentiment} (${c.sentiment_score})`}
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      lineHeight: '14px',
                      textAlign: 'center',
                      borderRadius: '50%',
                      background: sentDot.bg,
                      color: sentDot.fg,
                      fontSize: '0.65rem',
                      marginRight: 6,
                      fontWeight: 700,
                    }}
                  >{sentDot.label}</span>
                )}
                <span className="author">{c.author || 'anónimo'}</span>
                {c.text}
                {c.likes > 0 && <span className="likes">♥ {fmtNum(c.likes)}</span>}
                <span className="when">{fmtRel(c.posted_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </PostCard>
  );
};

/* ---------- componente principal ---------- */

const SocialProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loadState, setLoadState] = useState({ loading: true, error: null });
  const [commentsState, setCommentsState] = useState({ loading: false, lastResult: null, error: null });
  const [dummyAccounts, setDummyAccounts] = useState([]);
  const [ocrLoading, setOcrLoading] = useState(null); // post.id en curso
  const [ocrResult, setOcrResult] = useState(null);
  const [onlyPolitical, setOnlyPolitical] = useState(false);

  const loadPosts = async (filterPolitical = onlyPolitical) => {
    const postsData = await listSocialPosts(id, { withComments: true, limit: 60, onlyPolitical: filterPolitical });
    setPosts(postsData || []);
  };

  useEffect(() => {
    let cancelled = false;
    setLoadState({ loading: true, error: null });
    Promise.all([
      getSocialProfile(id),
      listSocialPosts(id, { withComments: true, limit: 60, onlyPolitical }),
      listDummyAccountsForOcr().catch(() => []),
    ])
      .then(([data, postsData, dummies]) => {
        if (cancelled) return;
        setProfile(data.profile);
        setSnapshots(data.snapshots || []);
        setPosts(postsData || []);
        setDummyAccounts(dummies || []);
        setLoadState({ loading: false, error: null });
      })
      .catch((e) => {
        if (!cancelled) setLoadState({ loading: false, error: friendlyError(e) });
      });
    return () => { cancelled = true; };
  }, [id, onlyPolitical]);

  // Pickea la primera dummy account active del platform del perfil actual.
  const pickDummyForCurrentPlatform = () => {
    if (!profile) return null;
    const match = dummyAccounts.find((d) => d.platform === profile.platform);
    return match || null;
  };

  const handleOcrPost = async (post) => {
    const dummy = pickDummyForCurrentPlatform();
    if (!dummy) {
      setOcrResult({ ok: false, error: `Necesitás un perfil digital ${profile.platform} con sessionid cargado. Andá a /perfiles-digitales.` });
      return;
    }
    if (!post.url) {
      setOcrResult({ ok: false, error: 'Post sin URL' });
      return;
    }
    setOcrLoading(post.id);
    setOcrResult(null);
    try {
      const r = await screenshotOcrComments(dummy.id, post.url);
      setOcrResult({ ok: true, ...r, dummy_username: dummy.username });
      // Refresco posts para que aparezcan los comments nuevos
      await loadPosts();
    } catch (err) {
      setOcrResult({ ok: false, error: friendlyError(err) });
    } finally {
      setOcrLoading(null);
    }
  };

  const handleFetchComments = async () => {
    setCommentsState({ loading: true, lastResult: null, error: null });
    try {
      const result = await fetchProfileComments(id, { limit: 5 });
      await loadPosts();
      let warning = null;
      if (result.comments_added === 0 && result.platform === 'instagram') {
        warning = 'Instagram no expone comentarios sin login — necesita cuenta dummy';
      }
      setCommentsState({ loading: false, lastResult: { ...result, warning }, error: null });
    } catch (err) {
      setCommentsState({
        loading: false,
        lastResult: null,
        error: friendlyError(err),
      });
    }
  };

  if (loadState.loading) {
    return (
      <TechShell>
        <TechPageHeader title="Cargando perfil…" dossier="SOC://PROFILE" />
        <Loading>Cargando</Loading>
      </TechShell>
    );
  }

  if (loadState.error || !profile) {
    return (
      <TechShell>
        <TechPageHeader title="Perfil no encontrado" dossier="SOC://PROFILE" />
        <ErrorBox>{loadState.error || 'No se encontró el perfil'}</ErrorBox>
        <TechButtonGhost type="button" onClick={() => navigate('/social')} style={{ marginTop: '1rem' }}>
          <FaArrowLeft /> Volver a Redes Sociales
        </TechButtonGhost>
      </TechShell>
    );
  }

  const meta = platformMeta(profile.platform);
  const Icon = meta.Icon;
  const latest = snapshots[0] || {};
  const previous = snapshots.find((s, i) => i > 0 && s.status === 'ok') || {};
  const delta = (typeof latest.followers === 'number' && typeof previous.followers === 'number')
    ? latest.followers - previous.followers
    : null;

  // Engagement: usa el valor persistido del snapshot; si falta, lo deriva de los
  // posts cargados (interacciones promedio sobre seguidores) para no mostrar vacío.
  let engagement = (latest.engagement_rate != null && Number.isFinite(Number(latest.engagement_rate)))
    ? Number(latest.engagement_rate)
    : null;
  if (engagement == null && latest.followers > 0) {
    const withLikes = posts.filter((p) => Number.isFinite(p.likes));
    if (withLikes.length) {
      const avg = withLikes.reduce((s, p) => s + (p.likes || 0) + (p.comments_count || 0) + (p.shares || 0), 0) / withLikes.length;
      const r = (avg / latest.followers) * 100;
      if (Number.isFinite(r)) engagement = Math.round(r * 1000) / 1000;
    }
  }

  return (
    <TechShell>
      <TechPageHeader
        title={profile.display_name || `@${profile.handle}`}
        subtitle={`@${profile.handle} · ${meta.label} · ${profile.kind || 'profile'}`}
        dossier={`SOC://${profile.platform.toUpperCase()}/${profile.id}`}
        actions={
          <>
            <TechButtonGhost as={Link} to="/social">
              <FaArrowLeft style={{ marginRight: 6 }} /> Volver
            </TechButtonGhost>
            {(profile.platform === 'instagram' || profile.platform === 'youtube') && posts.length > 0 && (
              <TechButtonGhost type="button" onClick={handleFetchComments} disabled={commentsState.loading}>
                <FaDownload style={{ marginRight: 6 }} />
                {commentsState.loading ? 'Trayendo comentarios…' : 'Cargar comentarios'}
              </TechButtonGhost>
            )}
            {profile.url && (
              <TechButtonGhost as="a" href={profile.url} target="_blank" rel="noopener noreferrer">
                <FaExternalLinkAlt style={{ marginRight: 6 }} /> Abrir en {meta.label}
              </TechButtonGhost>
            )}
          </>
        }
      >
        {profile.is_own && <StatusPill>Perfil <strong>propio</strong></StatusPill>}
        <StatusPill>{posts.length} <strong>posts indexados</strong></StatusPill>
        {latest.snapshot_at && (
          <StatusPill>Último snap <strong>{fmtRel(latest.snapshot_at)}</strong></StatusPill>
        )}
        {commentsState.lastResult && (
          commentsState.lastResult.warning ? (
            <StatusPill style={{ color: 'var(--alert-warning, #f59e0b)' }}>
              ⚠ {commentsState.lastResult.warning}
            </StatusPill>
          ) : (
            <StatusPill>+{commentsState.lastResult.comments_added} <strong>comentarios cargados</strong></StatusPill>
          )
        )}
        {commentsState.error && (
          <StatusPill style={{ color: 'var(--alert-error)' }}>Error · {commentsState.error}</StatusPill>
        )}
      </TechPageHeader>

      <StatStrip min="180px" style={{ marginBottom: '1.25rem' }}>
        <StatTile>
          <StatLabel><FaUsers /> Seguidores</StatLabel>
          <StatValue style={{ color: meta.color }}>{fmtNum(latest.followers)}</StatValue>
          {delta != null && delta !== 0 && (
            <StatSub style={{ color: delta > 0 ? 'var(--alert-success)' : 'var(--alert-error)' }}>
              {delta > 0 ? '+' : '−'}{fmtNum(Math.abs(delta))} vs anterior
            </StatSub>
          )}
        </StatTile>
        <StatTile>
          <StatLabel><FaImage /> Posts (perfil)</StatLabel>
          <StatValue>{fmtNum(latest.posts)}</StatValue>
          <StatSub>totales en la plataforma</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCommentDots /> Indexados acá</StatLabel>
          <StatValue>{posts.length}</StatValue>
          <StatSub>últimos recolectados</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaChartLine /> Engagement</StatLabel>
          <StatValue style={{ color: engagement != null ? meta.color : undefined }}>
            {engagement != null ? `${engagement}%` : '—'}
          </StatValue>
          <StatSub>{engagement != null ? 'interacciones / seguidores' : 'sin datos de likes'}</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaCheckCircle /> Snapshots</StatLabel>
          <StatValue>{snapshots.length}</StatValue>
          <StatSub>histórico</StatSub>
        </StatTile>
      </StatStrip>

      {ocrResult && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.75rem 1rem',
          background: panelDeep,
          border: `1px solid ${borderSoft}`,
          borderLeft: `2px solid ${ocrResult.ok ? 'var(--alert-success, #38d39f)' : 'var(--alert-error)'}`,
          borderRadius: 6,
          fontSize: '0.85rem',
          color: 'var(--text-secondary)',
        }}>
          <FaCamera style={{ marginRight: 6 }} />
          {ocrResult.ok ? (
            <>
              <strong>OCR completado</strong> con <code style={{ color: accent, fontFamily: mono }}>@{ocrResult.dummy_username}</code> ·
              {' '}{ocrResult.ocr_text_length} chars extraídos ·
              {' '}{ocrResult.comments_extracted} comments detectados ·
              {' '}{ocrResult.comments_added} guardados nuevos ·
              {' '}confianza {ocrResult.confidence}% ·
              {' '}{ocrResult.scrapingbee_credits_used || '?'} credits SB usados.
              {ocrResult.note && <div style={{ marginTop: 4, color: 'var(--text-disabled)' }}><em>{ocrResult.note}</em></div>}
            </>
          ) : (
            <><strong style={{ color: 'var(--alert-error)' }}>Error OCR:</strong> {ocrResult.error}</>
          )}
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: 10,
        alignItems: 'center',
        flexWrap: 'wrap',
        marginBottom: '1rem',
        padding: '0.7rem 0.9rem',
        background: panelDeep,
        border: `1px solid ${borderSoft}`,
        borderRadius: 6,
      }}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--text-primary)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={onlyPolitical}
            onChange={(e) => setOnlyPolitical(e.target.checked)}
          />
          <strong>Solo políticos</strong>
        </label>
        <span style={{ color: 'var(--text-disabled)', fontSize: '0.75rem', fontFamily: mono }}>
          · la IA analiza automáticamente cada nuevo post/comentario
        </span>
      </div>

      <TechSection
        title={`Posts · ${posts.length}${onlyPolitical ? ' (políticos)' : ''}`}
        action={
          profile.url && (
            <TechButtonGhost as="a" href={profile.url} target="_blank" rel="noopener noreferrer">
              <Icon style={{ color: meta.color, marginRight: 6 }} />
              Ver en {meta.label}
            </TechButtonGhost>
          )
        }
      >
        {posts.length === 0 ? (
          <Empty>
            Sin posts indexados todavía. El próximo cron diario de redes (03:00 ART)
            los traerá si la plataforma lo permite.
          </Empty>
        ) : (
          <PostsGrid>
            {posts.map((p) => (
              <PostItem
                key={p.id}
                post={p}
                onOcr={handleOcrPost}
                ocrLoading={ocrLoading}
                ocrAvailable={!!pickDummyForCurrentPlatform()}
              />
            ))}
          </PostsGrid>
        )}
      </TechSection>
    </TechShell>
  );
};

export default SocialProfileDetail;
